import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { z } from 'zod';
import contactHero from '@/assets/contact-hero.jpg';

// Validation schema
const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  phone: z.string().trim().max(50, "Phone must be less than 50 characters").optional(),
  company: z.string().trim().max(100, "Company must be less than 100 characters").optional(),
  subject: z.enum(['sales', 'support', 'careers', 'other']),
  message: z.string().trim().min(1, "Message is required").max(2000, "Message must be less than 2000 characters"),
});

const Contact = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: 'sales',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate form data
      const validatedData = contactSchema.parse(formData);

      // Send to edge function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify(validatedData),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send message');
      }

      toast({
        title: t("Επιτυχής Αποστολή", "Successful Submission"),
        description: t("Θα επικοινωνήσουμε μαζί σας το συντομότερο δυνατό.", "We will contact you as soon as possible."),
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: 'sales',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      
      if (error instanceof z.ZodError) {
        toast({
          title: t("Σφάλμα Επικύρωσης", "Validation Error"),
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: t("Σφάλμα", "Error"),
          description: t("Κάτι πήγε στραβά. Παρακαλώ δοκιμάστε ξανά.", "Something went wrong. Please try again."),
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={t('Επικοινωνία', 'Contact')}
        description={t('Επικοινωνήστε με την TFT για λύσεις επεξεργασίας και συσκευασίας τροφίμων. Τηλέφωνο, email και φόρμα επικοινωνίας.', 'Contact TFT for food processing and packaging solutions. Phone, email and contact form.')}
        canonical="https://www.tft.gr/contact"
      />
      <Navigation />

      <main>
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-primary to-primary-light text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold mb-6">{t('Επικοινωνήστε Μαζί Μας', 'Contact Us')}</h1>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              {t('Είμαστε εδώ για να απαντήσουμε σε κάθε σας ερώτηση', 'We are here to answer all your questions')}
            </p>
          </div>
        </section>

        {/* Hero Image below banner with CTA */}
        <section className="bg-background">
          <div className="container mx-auto px-4 py-12">
            <div className="relative rounded-2xl overflow-hidden shadow-elegant aspect-[21/9]">
              <img
                src={contactHero}
                alt={t('Εξυπηρέτηση πελατών TFT', 'TFT customer support')}
                width={1920}
                height={1080}
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/60 to-transparent" />
              <div className="absolute inset-0 flex items-center">
                <div className="px-8 md:px-16 max-w-2xl text-primary-foreground">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    {t('Είμαστε εδώ για εσάς', 'We are here for you')}
                  </h2>
                  <p className="text-base md:text-lg opacity-90 mb-6">
                    {t(
                      'Η ομάδα μας απαντά σε κάθε ερώτημα σχετικά με λύσεις επεξεργασίας και συσκευασίας τροφίμων.',
                      'Our team is ready to answer every question about food processing and packaging solutions.'
                    )}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button asChild size="lg" variant="secondary">
                      <a href="tel:+302130070100">
                        <Phone className="mr-2 h-5 w-5" />
                        213 00 70 100
                      </a>
                    </Button>
                    <Button asChild size="lg" variant="outline" className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                      <a href="#contact-form">
                        <Mail className="mr-2 h-5 w-5" />
                        {t('Στείλτε μήνυμα', 'Send a message')}
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="contact-form" className="py-20 bg-background scroll-mt-24">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Form */}
              <Card className="lg:col-span-2">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold mb-6">{t('Στείλτε μας μήνυμα', 'Send us a message')}</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">{t('Ονοματεπώνυμο', 'Full Name')} *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone">Τηλέφωνο</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="company">Εταιρεία</Label>
                        <Input
                          id="company"
                          value={formData.company}
                          onChange={(e) => setFormData({...formData, company: e.target.value})}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="subject">{t('Θέμα', 'Subject')} *</Label>
                      <Select value={formData.subject} onValueChange={(value) => setFormData({...formData, subject: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sales">{t('Πωλήσεις', 'Sales')}</SelectItem>
                          <SelectItem value="support">{t('Τεχνική Υποστήριξη', 'Technical Support')}</SelectItem>
                          <SelectItem value="careers">{t('Καριέρα', 'Careers')}</SelectItem>
                          <SelectItem value="other">{t('Άλλο', 'Other')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message">{t('Μήνυμα', 'Message')} *</Label>
                      <Textarea
                        id="message"
                        rows={6}
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        required
                      />
                    </div>

                    <Button type="submit" size="lg" className="w-full md:w-auto" disabled={isSubmitting}>
                      {isSubmitting ? t('Αποστολή...', 'Sending...') : t('Αποστολή Μηνύματος', 'Send Message')}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Contact Info */}
              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="mb-4 p-3 bg-primary/10 rounded-xl w-fit">
                      <MapPin className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold mb-2">{t('Διεύθυνση', 'Address')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('Παραδείσου 10, Μαρούσι 15125', 'Paradeisou 10 Marousi, 15125')}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="mb-4 p-3 bg-primary/10 rounded-xl w-fit">
                      <Phone className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold mb-2">{t('Τηλέφωνο', 'Phone')}</h3>
                    <a href="tel:+302130070100" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      213 00 70 100
                    </a>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="mb-4 p-3 bg-primary/10 rounded-xl w-fit">
                      <Mail className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold mb-2">Email</h3>
                    <a href="mailto:info@tft.gr" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      info@tft.gr
                    </a>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="mb-4 p-3 bg-primary/10 rounded-xl w-fit">
                      <Clock className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold mb-2">{t('Ώρες Λειτουργίας', 'Business Hours')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('Δευτέρα - Παρασκευή', 'Monday - Friday')}<br />
                      09:00 - 17:00
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;